import { CellClassParams, ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { AccessType, Scope } from "../../common/enums/DRCXEnums";
import { AccessPermission } from "../../common/models/AccessPermission";
import { IBaseGridColumnDefinition } from "../../common/models/BaseGridColumnDefinition";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { ActionMenuItem, BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { getAggregateFunction } from "../../components/ag-grid-extensions/AggregateHelper";
import { CellDef } from "../../components/ag-grid-extensions/models/CellDef";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import { IStandardCellRendererParams, StandardCellData } from "../../components/ag-grid-extensions/renderers/StandardCellRenderer";
import { IReportDataCell, IReportDataGridModel, IReportDataRow, ReportConfigurationDataModel, ReportConfigurationDescriptionModel } from "../../services/reporting-data-service/Models";
import { ReportViewActionEnum } from "./ReportViewActions";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import _ from "lodash";

export const writePermission: AccessPermission = new AccessPermission({
    functionalityType: AccessFunctionalityTypeEnum.Reporting,
    access: AccessTypeEnum.Write_Change
});

export function userHasPermission(
    configurationData: ReportConfigurationDataModel | undefined,
    permission: AccessPermission
): boolean {
    return (configurationData?.userPermissions)?.find(o => o.contains(permission)) !== undefined;
}

export function getConfiguration(
    configurationData: ReportConfigurationDataModel | undefined,
    configurationID: number | undefined
): ReportConfigurationDescriptionModel | undefined {
    return configurationData?.configurations?.find(o => o.id === configurationID);
}

export function getActions(
    sourceActions: ActionMenuItem[],
    isPhone: boolean,
    configurationID: number | undefined,
    configurationData: ReportConfigurationDataModel | undefined,
    canRunOrDownload: boolean,
    features: UIViewEnum[]
): ActionMenuItem[] {

    const actions = _.cloneDeep(sourceActions);

    const hasConfigurations = (configurationData?.configurations?.length ?? 0) > 0;
    const configuration = getConfiguration(configurationData, configurationID);

    const userHasWriteAccessToConfiguration = (configuration?.access === AccessType.Private) ||
        configuration?.userHasPermission(writePermission);

    const userHasModifyPermission = userHasPermission(configurationData, writePermission);

    let canEditConfiguration = false;
    let canDuplicateConfiguration = false;
    let canModifyConfigurationPermissions = false;
    let canDeleteConfiguration = false;
    let canRenameConfiguration = false;
    const canModify = hasConfigurations && configuration?.scope !== Scope.Custom;

    switch (configuration?.access) {

        case AccessType.Private:
            canEditConfiguration = canModify;
            canDuplicateConfiguration = canModify;
            canModifyConfigurationPermissions = canModify;
            canDeleteConfiguration = canModify;
            canRenameConfiguration = canModify;
            break;

        case AccessType.Shared:
            canEditConfiguration = userHasModifyPermission && canModify;
            canDuplicateConfiguration = canModify;
            canModifyConfigurationPermissions = userHasModifyPermission && canModify;
            canDeleteConfiguration = userHasModifyPermission && canModify;
            canRenameConfiguration = userHasModifyPermission && canModify;
            break;

        case AccessType.SharedReadOnly:
            canEditConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
            canDuplicateConfiguration = canModify;
            canModifyConfigurationPermissions = (userHasWriteAccessToConfiguration ?? false) && canModify;
            canDeleteConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
            canRenameConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
            break;

        default:
            break;
    }

    const configurationSelected = (configuration?.id ?? -1) > -1;

    // fist determine which actions are visible
    const visibleActions = actions.filter(action => {
        switch (action.action) {
            case BaseActionEnum.Run:
                return configurationSelected;

            case BaseActionEnum.Export:
                return configurationSelected;

            case BaseActionEnum.Configure:
                if (isPhone) {
                    return false;
                }

                action.subActions = (action.subActions ?? []).filter(o => {
                    if (o.action === BaseActionEnum.CreateNew || o.action === BaseActionEnum.Edit) {
                        return true;
                    } else if (o.action === BaseActionEnum.SetAsDefault) {
                        return false;
                    }

                    return configurationSelected;
                })

                return true;

            case ReportViewActionEnum.MyScheduledReports:
                return features.some(o => o === UIViewEnum.MyScheduledReports);

            default:
                return true;
        }
    });

    // next determine the state of the visible actions
    return visibleActions
        .map(action => {
            const actionCopy = { ...action };

            switch (action.action) {
                case BaseActionEnum.Run:
                    actionCopy.disabled = !canRunOrDownload;
                    break;

                case BaseActionEnum.Export:
                    actionCopy.disabled = !canRunOrDownload;
                    break;

                case BaseActionEnum.Configure:
                    actionCopy.subActions = action.subActions?.map(subAction => {
                        const copy = { ...subAction };

                        switch (subAction.action) {

                            case BaseActionEnum.Edit:
                                copy.disabled = !canEditConfiguration;
                                break;

                            case BaseActionEnum.CreateNew:
                                // should always be enabled
                                copy.disabled = false;
                                break;

                            case BaseActionEnum.Duplicate:
                                copy.disabled = !canDuplicateConfiguration;
                                break;

                            case BaseActionEnum.ModifyPermissions:
                                copy.disabled = !canModifyConfigurationPermissions;
                                break;

                            case BaseActionEnum.Delete:
                                copy.disabled = !canDeleteConfiguration;
                                break;

                            case BaseActionEnum.Rename:
                                copy.disabled = !canRenameConfiguration;
                                break;

                            default:
                                copy.disabled = !canModify;
                        }

                        return copy;
                    });
            }

            return actionCopy;
        });
}

export function getActionsV2(
    sourceActions: ActionMenuItem[],
    isPhone: boolean,
    configurationID: number | undefined,
    configurationData: ReportConfigurationDataModel | undefined,
    canRunOrDownload: boolean,
    features: UIViewEnum[]
): ActionMenuItem[] {

    const actions = _.cloneDeep(sourceActions);

    const hasConfigurations = (configurationData?.configurations?.length ?? 0) > 0;
    const configuration = getConfiguration(configurationData, configurationID);

    const userHasWriteAccessToConfiguration = (configuration?.access === AccessType.Private) ||
        configuration?.userHasPermission(writePermission);

    const userHasModifyPermission = userHasPermission(configurationData, writePermission);

    let canEditConfiguration = false;
    let canDuplicateConfiguration = false;
    let canModifyConfigurationPermissions = false;
    let canDeleteConfiguration = false;
    let canRenameConfiguration = false;
    const canModify = hasConfigurations && configuration?.scope !== Scope.Custom;

    switch (configuration?.access) {

        case AccessType.Private:
            canEditConfiguration = canModify;
            canDuplicateConfiguration = canModify;
            canModifyConfigurationPermissions = canModify;
            canDeleteConfiguration = canModify;
            canRenameConfiguration = canModify;
            break;

        case AccessType.Shared:
            canEditConfiguration = userHasModifyPermission && canModify;
            canDuplicateConfiguration = canModify;
            canModifyConfigurationPermissions = userHasModifyPermission && canModify;
            canDeleteConfiguration = userHasModifyPermission && canModify;
            canRenameConfiguration = userHasModifyPermission && canModify;
            break;

        case AccessType.SharedReadOnly:
            canEditConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
            canDuplicateConfiguration = canModify;
            canModifyConfigurationPermissions = (userHasWriteAccessToConfiguration ?? false) && canModify;
            canDeleteConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
            canRenameConfiguration = (userHasWriteAccessToConfiguration ?? false) && canModify;
            break;

        default:
            break;
    }

    const configurationSelected = (configuration?.id ?? -1) > -1;

    // fist determine which actions are visible
    // const visibleActions = actions.filter(action => {
    //     switch (action.action) {
    //         case BaseActionEnum.Run:
    //             return configurationSelected;

    //         case BaseActionEnum.Export:
    //             return configurationSelected;

    //         case BaseActionEnum.Configure:
    //             if (isPhone) {
    //                 return false;
    //             }

    //             action.subActions = (action.subActions ?? []).filter(o => {
    //                 if (o.action === BaseActionEnum.CreateNew || o.action === BaseActionEnum.Edit) {
    //                     return true;
    //                 } else if (o.action === BaseActionEnum.SetAsDefault) {
    //                     return false;
    //                 }

    //                 return configurationSelected;
    //             })

    //             return true;

    //         case ReportViewActionEnum.MyScheduledReports:
    //             return features.some(o => o === UIViewEnum.MyScheduledReports);

    //         default:
    //             return true;
    //     }
    // });

    // // next determine the state of the visible actions
    // return visibleActions
    //     .map(action => {
    //         const actionCopy = { ...action };

    //         switch (action.action) {
    //             case BaseActionEnum.Run:
    //                 actionCopy.disabled = !canRunOrDownload;
    //                 break;

    //             case BaseActionEnum.Export:
    //                 actionCopy.disabled = !canRunOrDownload;
    //                 break;

    //             case BaseActionEnum.Configure:
    //                 actionCopy.subActions = action.subActions?.map(subAction => {
    //                     const copy = { ...subAction };

    //                     switch (subAction.action) {

    //                         case BaseActionEnum.Edit:
    //                             copy.disabled = !canEditConfiguration;
    //                             break;

    //                         case BaseActionEnum.CreateNew:
    //                             // should always be enabled
    //                             copy.disabled = false;
    //                             break;

    //                         case BaseActionEnum.Duplicate:
    //                             copy.disabled = !canDuplicateConfiguration;
    //                             break;

    //                         case BaseActionEnum.ModifyPermissions:
    //                             copy.disabled = !canModifyConfigurationPermissions;
    //                             break;

    //                         case BaseActionEnum.Delete:
    //                             copy.disabled = !canDeleteConfiguration;
    //                             break;

    //                         case BaseActionEnum.Rename:
    //                             copy.disabled = !canRenameConfiguration;
    //                             break;

    //                         default:
    //                             copy.disabled = !canModify;
    //                     }

    //                     return copy;
    //                 });
    //         }

    //         return actionCopy;
    //     });
   
    // next determine the state of the visible actions
    actions.forEach(action => {
        const actionCopy = { ...action };

        switch (action.action) {
            case BaseActionEnum.Run:
                actionCopy.disabled = !canRunOrDownload || !configurationSelected;
                break;

            case ReportViewActionEnum.MyScheduledReports:
                actionCopy.disabled = !features.some(o => o === UIViewEnum.MyScheduledReports);
                break;

            case BaseActionEnum.Configure: {

                if (isPhone) {
                    actionCopy.disabled = true;
                } else {
                    action.subActions?.forEach(o => {


                        switch (o.action) {

                            case BaseActionEnum.Edit:
                                o.disabled = !canEditConfiguration || !configurationSelected;
                                break;

                            case BaseActionEnum.CreateNew:
                                // should always be enabled
                                o.disabled = false;
                                break;

                            case BaseActionEnum.Duplicate:
                                o.disabled = !canDuplicateConfiguration || !configurationSelected;
                                break;

                            case BaseActionEnum.ModifyPermissions:
                                o.disabled = !canModifyConfigurationPermissions || !configurationSelected;
                                break;

                            case BaseActionEnum.Delete:
                                o.disabled = !canDeleteConfiguration || !configurationSelected;
                                break;

                            case BaseActionEnum.Rename:
                                o.disabled = !canRenameConfiguration || !configurationSelected;
                                break;

                            case BaseActionEnum.SetAsDefault:
                                o.disabled = !configurationSelected;
                                break;
                                
                            default:
                                o.disabled = !canModify;
                        }
                    });
                }
            }
            break;
        }

            return actionCopy;
    });

    return actions;
}

export function createGridData(
    gridModel: IReportDataGridModel | undefined
) {
    const cols: Array<ColDef> = createGridColumns(gridModel);
    const rows: Array<RowDef> = createGridRows(gridModel/*, gridModel?.showFooter*/);
    const data = cols && rows
        ? {
            rows: rows,
            cols: cols
        } : {
            rows: new Array<RowDef>(),
            cols: new Array<ColDef>()
        };
    return data;
}

export function createGridRows(gridModel: IReportDataGridModel | undefined/*, showFooter: boolean | undefined*/): Array<RowDef> {

    // Bug 51535: React - Reporting - Last Object Not Displayed On Screen - not sure why the last row was removed when showFooter is true
    // Bug 52287: BUG - Reporting - Options... now the previous bug 51535 is not an issue
    // const rows = showFooter
    //     ? gridModel?.rows.slice(0, -1)
    //     : gridModel?.rows;
    //Bug 52553: Reporting - "Include final row with totals" - exists in SCADALynx, but not React. For reporting the totals are included in the data returned so do not slice the rows
    const rows = gridModel?.rows;

    return rows?.map((row: IReportDataRow) => {

        const newRow: RowDef = {
            rowID: row.rowID,
            active: row.active,
        };

        row.cells.forEach((cell: IReportDataCell, index: number) => {
            const displayText = getDisplayText(cell);
            newRow[`cell${index}`] = cell;
            newRow[`cell${index}`].displayText = displayText;
        });

        return newRow;
    }) ?? new Array<RowDef>();
}

export function createGridColumns(gridModel: IReportDataGridModel | undefined): Array<ColDef> {

    const cols = gridModel?.columns
        .map((col: IBaseGridColumnDefinition, index) => {
            const field = `cell${index}`;

            if (col.field.indexOf("value") !== -1) {

                // Numeric Column
                return {
                    headerName: col.headerText,
                    cellRenderer: "standardCellRenderer",
                    cellRendererParams: {
                        // eslint-disable-next-line no-new-func
                        cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: ICellRendererParams) => CellDef,
                    },
                    // eslint-disable-next-line no-new-func
                    valueGetter: new Function("params", `return params.data?.${field}?.value;`) as (params: ValueGetterParams) => number,
                    sortable: true,
                    field: field,
                    aggFunc: getAggregateFunction(col.aggregate, col.decimalPlaces),
                    type: "number"
                };
            } else if (col.field.indexOf("timestamp") !== -1) {

                // Timestamp column
                return {
                    headerName: col.headerText,
                    cellRenderer: "standardCellRenderer",
                    cellRendererParams: {
                        // eslint-disable-next-line no-new-func
                        cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: ICellRendererParams) => CellDef,
                    },
                    // eslint-disable-next-line no-new-func
                    valueGetter: new Function("params", `return params.data?.${field}?.displayTimestamp?.timestamp;`) as (params: ValueGetterParams) => number,
                    sortable: true,
                    field: field,
                    type: "timestamp"
                };
            } else {

                const isNameColumn = col.headerText.toLowerCase().includes('device display name') ||
                    col.headerText.toLowerCase().includes('device name') ||
                    col.headerText.toLowerCase().includes('well name');
                // Text Column
                return {
                    headerName: col.headerText,
                    cellRenderer: "standardCellRenderer",
                    cellStyle: (params: CellClassParams) => {

                        return {
                            'fontStyle': isNameColumn && params?.data?.active === false ? 'italic' : 'inherit',
                        };

                    },

                    cellRendererParams: {
                        // eslint-disable-next-line no-new-func
                        cellDataGetter: new Function("params", `return params.data?.${field};`) as (params: IStandardCellRendererParams) => StandardCellData,
                    },
                    // eslint-disable-next-line no-new-func
                    valueGetter: new Function("params", `return params.data?.${field}?.displayText;`) as (params: ValueGetterParams) => string,
                    sortable: true,
                    field: field,
                    aggFunc: getAggregateFunction(col.aggregate),
                    type: "text"
                };
            }

        }) ?? new Array<ColDef>();

    return cols;
}

export function getDisplayText(cell: IReportDataCell): string {
    let text: string = cell?.displayText ?? "";

    // If this is a timestamp cell, then we need to make sure it's formatted properly
    if (typeof cell.displayTimestamp?.timestamp === "string" && typeof cell.displayTimestamp.timeframeType === "number") {
        text = getTimeframeTimeDisplayText(cell.displayTimestamp.timeframeType, cell.displayTimestamp.timestamp);
    }

    return text;
}

